import React from "react";
import PageHero from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { Backdrop } from "@/components/IntersectMotif";
import { FinalCTA } from "@/pages/Home";
import { IMG, PARTNERSHIP_MODELS, PARTNERSHIP_FLOW } from "@/lib/content";
import { useLang } from "@/lib/i18n";
import { ArrowDown } from "lucide-react";

export default function Partnerships() {
  const { t } = useLang();
  return (
    <>
      <PageHero eyebrow="Partnerships" title="Some opportunities require more than one company." image={IMG.boardroom}
        subtitle="We bring together organizations whose capabilities become more valuable when combined." />

      <section className="section-pad relative overflow-hidden">
        <Backdrop src={IMG.skylineSunset} opacity={0.1} />
        <div className="container-x relative z-10 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4"><Reveal><p className="eyebrow mb-6">{t("Our Role")}</p></Reveal>
            <Reveal delay={0.1}><h2 className="text-2xl md:text-4xl font-light tracking-tight text-ivory">{t("Creating alliances that create value.")}</h2></Reveal></div>
          <div className="lg:col-span-7 lg:col-start-6 space-y-6 text-lg leading-relaxed text-soft-stone">
            <Reveal delay={0.15}><p>{t("Intersect identifies organizations with complementary capabilities and aligns their interests to create strategic alliances, joint ventures and commercial partnerships. We work across borders and sectors — connecting global capability with Saudi opportunity.")}</p></Reveal>
          </div>
        </div>
      </section>

      <section className="section-pad bg-charcoal relative overflow-hidden">
        <Backdrop src={IMG.faisaliah} opacity={0.1} />
        <div className="container-x relative z-10">
          <Reveal><p className="eyebrow mb-6">{t("Partnership Models")}</p></Reveal>
          <Reveal delay={0.1}><h2 className="text-2xl md:text-4xl font-light tracking-tight text-ivory mb-14">{t("The forms partnership can take.")}</h2></Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-primary-gold/10 border border-primary-gold/10">
            {PARTNERSHIP_MODELS.map((m, i) => (
              <Reveal key={m}><div className="card-lift bg-charcoal p-8 min-h-[140px] md:min-h-[130px] flex items-end hover:bg-obsidian transition-colors group" data-testid={`pmodel-${i}`}>
                <span className="text-primary-gold text-xs tracking-widest mr-4">{String(i + 1).padStart(2, "0")}</span>
                <span className="text-lg font-light text-ivory group-hover:text-primary-gold transition-colors">{t(m)}</span>
              </div></Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="section-pad relative overflow-hidden">
        <Backdrop src={IMG.geometric} opacity={0.08} />
        <div className="container-x relative z-10">
          <Reveal><p className="eyebrow mb-6">{t("The Partnership Model")}</p></Reveal>
          <Reveal delay={0.1}><h2 className="text-2xl md:text-4xl font-light tracking-tight text-ivory mb-14">{t("From opportunity to execution.")}</h2></Reveal>
          <div className="max-w-2xl">
            {PARTNERSHIP_FLOW.map((step, i) => (
              <Reveal key={step} delay={0.05 * i}>
                <div className="flex items-center gap-6" data-testid={`flow-${i}`}>
                  <div className="flex-1 border border-primary-gold/15 bg-charcoal px-8 py-6 flex items-center gap-6">
                    <span className="text-primary-gold text-sm tracking-[0.25em]">{String(i + 1).padStart(2, "0")}</span>
                    <span className="text-xl font-light text-ivory">{t(step)}</span>
                  </div>
                </div>
                {i < PARTNERSHIP_FLOW.length - 1 && <div className="ml-12 my-2"><ArrowDown className="text-primary-gold/50" size={18} strokeWidth={1.5} /></div>}
              </Reveal>
            ))}
          </div>
        </div>
      </section>
      <FinalCTA />
    </>
  );
}
